import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { InputAdornment, TextField, TextFieldProps } from "@mui/material";
import { matchesDropdownSearch } from "./dropdownSearchMatch";

// Claude Design CMP Dashboard (20) CLAUDE.md "Country dropdown (global)" and
// ds-components/country-select.js (IF10 D7): every staff Country field is one
// searchable dropdown. Placeholder "Select Country", Canada and United States
// first, then the design list, starts-with matching, and only list values
// can be picked. A saved value that is not in the list (older free text)
// still shows and saves unchanged until the user picks a list value.

const TOP_COUNTRIES = ["Canada", "United States"] as const;
// Copied verbatim from ds-components/country-select.js REST.
const DESIGN_COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Armenia", "Australia", "Austria", "Bahamas", "Bangladesh", "Barbados",
  "Belgium", "Belize", "Bermuda", "Bolivia", "Brazil", "Bulgaria", "Cambodia", "Chile", "China", "Colombia",
  "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Dominican Republic", "Ecuador", "Egypt", "El Salvador",
  "Estonia", "Ethiopia", "Fiji", "Finland", "France", "Germany", "Ghana", "Greece", "Guatemala", "Haiti",
  "Honduras", "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
  "Italy", "Jamaica", "Japan", "Jordan", "Kenya", "Kuwait", "Latvia", "Lebanon", "Lithuania", "Luxembourg",
  "Malaysia", "Malta", "Mexico", "Morocco", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Nigeria", "Norway",
  "Pakistan", "Panama", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Puerto Rico", "Qatar", "Romania",
  "Saudi Arabia", "Serbia", "Singapore", "Slovakia", "Slovenia", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sweden",
  "Switzerland", "Taiwan", "Thailand", "Trinidad and Tobago", "Turkey", "Ukraine", "United Arab Emirates", "United Kingdom", "Uruguay", "Venezuela",
  "Vietnam",
] as const;

export const CMP_COUNTRY_OPTIONS: readonly string[] = [
  ...TOP_COUNTRIES,
  ...DESIGN_COUNTRIES,
];

export const COUNTRY_SELECT_PLACEHOLDER = "Select Country";

export const filterCountryOptions = (query: string): string[] =>
  query.trim()
    ? CMP_COUNTRY_OPTIONS.filter((country) => matchesDropdownSearch(country, query))
    : [...CMP_COUNTRY_OPTIONS];

// Design country-select.js close(commit): an exact name (any case) becomes
// that list value; a text with one match becomes that match; an empty text
// clears the field; any other text returns to the value before typing.
export const resolveCountryEntry = (text: string, previous: string): string => {
  const query = text.trim();
  if (!query) return "";
  const exact = CMP_COUNTRY_OPTIONS.find(
    (country) => country.toLowerCase() === query.toLowerCase(),
  );
  if (exact) return exact;
  const matches = filterCountryOptions(query);
  return matches.length === 1 ? matches[0] : previous;
};

const chevronBackground =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23727272' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")";

const ChevronIcon = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#727272" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

type PopupPosition = {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
};

export type CountrySelectFieldProps = Omit<
  TextFieldProps,
  "value" | "onChange" | "select" | "children" | "defaultValue"
> & {
  value: string;
  onValueChange: (value: string) => void;
  // Renders a plain <input> with these props (for forms that do not use
  // MUI fields), like GoogleAddressTextField.
  nativeInputProps?: React.InputHTMLAttributes<HTMLInputElement>;
};

export default function CountrySelectField({
  value,
  onValueChange,
  nativeInputProps,
  ...field
}: CountrySelectFieldProps) {
  const generatedId = useId().replace(/:/g, "");
  const listId = `${field.id || `country-${generatedId}`}-options`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  // Text the user typed since the list opened; null while not typing.
  const [draft, setDraft] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState<PopupPosition | null>(null);
  const readOnly = Boolean(nativeInputProps?.readOnly || field.InputProps?.readOnly);
  const disabled = Boolean(field.disabled || nativeInputProps?.disabled);
  const typing = draft !== null;
  const options = typing ? filterCountryOptions(draft) : [...CMP_COUNTRY_OPTIONS];

  const anchor = () =>
    inputRef.current?.closest<HTMLElement>(".MuiInputBase-root") ?? inputRef.current;

  const placePopup = useCallback(() => {
    const element =
      inputRef.current?.closest<HTMLElement>(".MuiInputBase-root") ?? inputRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const maxHeight = Math.max(120, Math.min(280, Math.max(below, rect.top) - 16));
    const showBelow = below >= 160 || below >= rect.top;
    setPosition({
      left: rect.left,
      width: Math.max(rect.width, 200),
      maxHeight,
      ...(showBelow
        ? { top: rect.bottom + 6 }
        : { bottom: window.innerHeight - rect.top + 6 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    placePopup();
    window.addEventListener("resize", placePopup);
    window.addEventListener("scroll", placePopup, true);
    return () => {
      window.removeEventListener("resize", placePopup);
      window.removeEventListener("scroll", placePopup, true);
    };
  }, [open, placePopup]);

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-option-index="${activeIndex}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setDraft(null);
    }
  }, [disabled]);

  const openList = () => {
    if (disabled || readOnly || open) return;
    const index = CMP_COUNTRY_OPTIONS.findIndex(
      (country) => country.toLowerCase() === value.trim().toLowerCase(),
    );
    setDraft(null);
    setActiveIndex(index > -1 ? index : 0);
    setOpen(true);
  };

  const commitValue = (next: string) => {
    if (next !== value) onValueChange(next);
  };

  // Closing without a pick keeps a saved value unchanged unless the user typed.
  const closeList = (commit: boolean) => {
    if (commit && draft !== null) commitValue(resolveCountryEntry(draft, value));
    setDraft(null);
    setOpen(false);
  };

  const pick = (country: string) => {
    commitValue(country);
    setDraft(null);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled || readOnly) return;
    if (!open) {
      if (event.key === "ArrowDown" || event.key === "Enter") {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) =>
        Math.min(Math.max(current + step, 0), Math.max(options.length - 1, 0)),
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (options[activeIndex]) pick(options[activeIndex]);
    } else if (event.key === "Escape") {
      // Close only the list; an enclosing dialog stays open.
      event.preventDefault();
      event.stopPropagation();
      closeList(false);
    } else if (event.key === "Tab") {
      closeList(true);
    }
  };

  const inputHandlers = {
    value: draft ?? value,
    placeholder: field.placeholder || COUNTRY_SELECT_PLACEHOLDER,
    onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
      openList();
      nativeInputProps?.onFocus?.(event);
    },
    onClick: () => openList(),
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const text = event.target.value;
      if (!open) openList();
      setOpen(true);
      setDraft(text);
      setActiveIndex(0);
      // A full country name is saved at once, so a save that does not blur
      // the field (for example Enter in a form) keeps it.
      const exact = CMP_COUNTRY_OPTIONS.find(
        (country) => country.toLowerCase() === text.trim().toLowerCase(),
      );
      if (exact) commitValue(exact);
    },
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
      if (open) closeList(true);
      field.onBlur?.(event as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
      nativeInputProps?.onBlur?.(event);
    },
    onKeyDown: handleKeyDown,
  };

  const comboboxProps = {
    role: "combobox",
    autoComplete: "off",
    "aria-autocomplete": "list" as const,
    "aria-expanded": open,
    "aria-haspopup": "listbox" as const,
    "aria-controls": open ? listId : undefined,
    "aria-activedescendant":
      open && options[activeIndex] ? `${listId}-${activeIndex}` : undefined,
  };

  const selectedKey = value.trim().toLowerCase();
  const popup =
    open && position
      ? createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label="Countries"
            data-country-select-popup
            className="fixed z-[11000] box-border overflow-y-auto"
            style={{
              ...position,
              background: "#FFFFFF",
              border: "1px solid #D4D4D4",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,.12)",
              fontFamily: "'DM Sans', sans-serif",
              padding: "4px 0",
            }}
            onMouseDown={(event) => event.preventDefault()}
          >
            {options.length === 0 ? (
              <div style={{ padding: "10px 14px", fontSize: 14, color: "#727272" }}>
                No matches found
              </div>
            ) : (
              options.map((country, index) => {
                const selected = country.toLowerCase() === selectedKey;
                const separator = !typing && index === TOP_COUNTRIES.length;
                return (
                  <div
                    key={country}
                    id={`${listId}-${index}`}
                    data-option-index={index}
                    role="option"
                    aria-selected={selected}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseMove={() => {
                      if (index !== activeIndex) setActiveIndex(index);
                    }}
                    onClick={() => pick(country)}
                    style={{
                      alignItems: "center",
                      background: index === activeIndex ? "#FAF1E1" : "#FFFFFF",
                      color: "#0A0A0A",
                      cursor: "pointer",
                      display: "flex",
                      fontSize: 14,
                      fontWeight: selected ? 600 : 400,
                      gap: 10,
                      justifyContent: "space-between",
                      padding: "9px 14px",
                      ...(separator
                        ? { borderTop: "1px solid #ECECEC", marginTop: 4, paddingTop: 12 }
                        : {}),
                    }}
                  >
                    <span>{country}</span>
                    {selected ? (
                      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="#E1A731" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0 }}>
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>,
          anchor()?.ownerDocument.body ?? document.body,
        )
      : null;

  if (nativeInputProps) {
    return (
      <>
        <input
          {...nativeInputProps}
          {...comboboxProps}
          {...inputHandlers}
          ref={inputRef}
          id={field.id}
          type="text"
          disabled={disabled}
          aria-invalid={field.error || nativeInputProps["aria-invalid"]}
          style={{
            backgroundImage: chevronBackground,
            backgroundPosition: "right 12px center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "16px 16px",
            cursor: readOnly ? "default" : "pointer",
            paddingRight: 38,
            ...nativeInputProps.style,
          }}
        />
        {popup}
      </>
    );
  }

  return (
    <>
      <TextField
        {...field}
        disabled={disabled}
        inputRef={inputRef}
        value={inputHandlers.value}
        placeholder={inputHandlers.placeholder}
        onFocus={inputHandlers.onFocus}
        onClick={inputHandlers.onClick}
        onChange={inputHandlers.onChange}
        onBlur={inputHandlers.onBlur}
        onKeyDown={inputHandlers.onKeyDown}
        InputProps={{
          ...field.InputProps,
          endAdornment: (
            <InputAdornment position="end" sx={{ pointerEvents: "none" }}>
              <ChevronIcon />
            </InputAdornment>
          ),
        }}
        inputProps={{
          ...field.inputProps,
          ...comboboxProps,
          style: { cursor: readOnly ? "default" : "pointer", ...field.inputProps?.style },
        }}
      />
      {popup}
    </>
  );
}
